/**
 * Shared accept/size validation for `FileDropZone` consumers (LF-6124).
 *
 * `FileDropZone` itself forwards a drop unvalidated by design — "drag accepts
 * everything, then validates" (see `CLAUDE.md`) — so `useFileDropZoneState`
 * (mode A) is the only caller that gets this check for free. A mode B/C
 * caller (a controlled `files` array, or upload-on-submit) owns its own file
 * state and must call this itself, or a dropped file that a picker would have
 * filtered sails straight through to upload.
 */

import { formatFileSize } from '@/lib/services/files';

/** Does `file` satisfy the native-style `accept` string? Empty accept = all. */
export function matchesFileAccept(file: File, accept?: string): boolean {
  const tokens = (accept ?? '')
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return tokens.some((tok) => {
    if (tok === '*/*') return true;
    if (tok.startsWith('.')) return name.endsWith(tok);
    if (tok.endsWith('/*')) return type.startsWith(tok.slice(0, -1));
    return type === tok;
  });
}

/**
 * Extension → MIME type for the formats a browser routinely reports with an
 * empty or wrong `file.type`. This is the same unreliability `matchesFileAccept`
 * above already works around by matching extension tokens — the difference is
 * that a client-side `accept` check can shrug at a blank type and still let the
 * file through, whereas anything we SEND the type to cannot.
 */
const MIME_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.eml': 'message/rfc822',
  '.msg': 'application/vnd.ms-outlook'
};

/**
 * The MIME type to send to an API for `file`, falling back to its extension
 * when the browser reports none.
 *
 * `file.type` is empty far more often than it looks — HEIC has no registered
 * type on Windows and most Linux browsers, and `.msg` only resolves where
 * Outlook is installed. Posting that empty string to an endpoint that validates
 * the MIME against a fixed list is an instant, unexplained rejection: it is how
 * a client's death-certificate photo was refused four times by
 * `POST /portal/intake-form/upload` with nothing shown on screen (VALE-925).
 *
 * Call this at every upload boundary rather than passing `file.type` raw.
 */
export function resolveUploadMimeType(file: File): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  const extension = Object.keys(MIME_BY_EXTENSION).find((ext) =>
    name.endsWith(ext)
  );
  return extension ? MIME_BY_EXTENSION[extension] : '';
}

export interface FileAcceptConstraint {
  accept?: string;
  maxSizeBytes?: number;
}

/**
 * Validate one file against an accept/size constraint. Returns an error
 * message, or `undefined` if the file is acceptable.
 */
export function validateFileAccept(
  file: File,
  { accept, maxSizeBytes }: FileAcceptConstraint
): string | undefined {
  if (maxSizeBytes && file.size > maxSizeBytes) {
    return `File is too large. Maximum ${formatFileSize(maxSizeBytes)}`;
  }
  if (!matchesFileAccept(file, accept)) {
    return 'Unsupported file type';
  }
  return undefined;
}
